// @ts-check
import React, { useEffect, useRef, useState } from "react"
import VacationsHeader from "../common/VacationsHeader.controller"
import TeamVacationRequests from "./TeamVacationRequests.controller"
import CalendarTeamVacationRequests from "./CalendarTeamVacationRequests.controller"
import AdminVacationsResume from "./AdminVacationsResume.controller"
import {
  TEAM_VACATION_REQUESTS_FILTER_TYPES,
  VACATION_PERIOD_NAVIGATOR_MODES,
  VACATION_STATES,
} from "../../../../consts/modules/vacations.consts"
import { getNavigableVacationsManagementPeriod } from "../../../../utils/date.utils"
import { useSession } from "../../../../hooks/auth/useSession.hook"
import { DateTime } from "luxon"
import ModalFormVacationRequestReview from "./ModalFormVacationRequestReview.controller"
import ModalFormApprovedRequestReview from "./ModalFormApprovedRequestReview.controller"
import { getVacationState } from "../../../../utils/vacation.utils"
import ModalFormRejectedRequestReview from "./ModalFormRejectedRequestReview.controller"
import ModalFormChangeRequestReview from "./ModalFormChangeRequestReview.controller"
import ModalFormCancelRequestReview from "./ModalFormCancelRequestReview.controller"
import {
  getAdminVacationResume,
  getYearHolidays,
  getAdminVacationResumeByMonth,
} from "../../../../services/vacation.service"
import ModalFormHolidaysManagement from "./ModalFormHolidaysManagement.controller"
import useToast from "../../../../hooks/useToast.hook"

/** @typedef {import("../../../../consts/modules/vacations.consts")["TEAM_VACATION_REQUESTS_FILTER_TYPES"]} TEAM_VACATION_REQUESTS_FILTER_TYPES */
/** @typedef {import("../../../../services/vacation.service.types").getTeamVacationsRequestsByYearResponse} TeamVacationRequest */
/** @typedef {TEAM_VACATION_REQUESTS_FILTER_TYPES[keyof TEAM_VACATION_REQUESTS_FILTER_TYPES]} SelectedVacationType */
/** @typedef {import("../../../../services/vacation.service.types").GetAdminVacationResumeResponse} vacationsTotalsSummary */
/** @typedef {typeof VACATION_STATES[keyof typeof VACATION_STATES]} vacationStates */

const VacationsManagement = ({
  mode = VACATION_PERIOD_NAVIGATOR_MODES.MANAGEMENT,
}) => {
  const [isLoadingTotalsSummary, setIsLoadingTotalsSummary] = useState(false)
  const [totalsSummary, setTotalsSummary] = useState(
    /** @type {vacationsTotalsSummary} */
    ({
      pendingRequests: 0,
      changeRequests: 0,
      daysPlusToPayRequests: 0,
      holidaysQuantity: 0,
    })
  )
  const [selectedPeriod, setSelectedPeriod] = useState(
    getNavigableVacationsManagementPeriod()
  )

  const resetSelectedPeriod = () => {
    setSelectedPeriod(getNavigableVacationsManagementPeriod())
  }

  const [refresh, setRefresh] = useState(false)
  const [visibleModal, setVisibleModal] = useState(false)
  const [selectedVacationRequest, setSelectedVacationRequest] = useState(
    /** @type {TeamVacationRequest | null} */
    (null)
  )
  const [isHolidaysModalVisible, setIsHolidaysModalVisible] = useState(false)
  const triggerRefresh = () => setRefresh(!refresh)
  const { user } = useSession()

  const fetchData = async () => {
    setIsLoadingTotalsSummary(true)
    try {
      const response = await getAdminVacationResume(selectedPeriod[0].year)
      if (!response.success) {
        throw new Error(response.message)
      }
      const result = /** @type {vacationsTotalsSummary} */ (response.result)
      setTotalsSummary(result)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setIsLoadingTotalsSummary(false)
    }
  }

  const [selectedVacationType, setSelectedVacationType] = useState(
    /** @type {SelectedVacationType} */
    (TEAM_VACATION_REQUESTS_FILTER_TYPES.PENDING)
  )

  useEffect(() => {
    if (
      mode === VACATION_PERIOD_NAVIGATOR_MODES.PERIOD &&
      user?.workAnniversary
    ) {
      const anniversaryDate = DateTime.fromISO(user.workAnniversary)
      const currentYear = DateTime.local().year
      setSelectedPeriod([
        anniversaryDate.set({ year: currentYear - 1 }),
        anniversaryDate.set({ year: currentYear }),
      ])
    }
  }, [mode, user])

  /** @type {(visibleModal: boolean, selectedVacationRequest: TeamVacationRequest | null, keys: vacationStates[]) => boolean} */
  const showModal = (visibleModal, selectedVacationRequest, keys) => {
    if (!visibleModal || !selectedVacationRequest || !keys) return false
    const vacationStatus = getVacationState(selectedVacationRequest)
    return visibleModal && keys.includes(vacationStatus)
  }

  const [previousMonthDate, setPreviousMonthDate] = useState(null)
  useEffect(() => {
    fetchData()
    setPreviousMonthDate(null)
    setSelectedFilteredCalendarVacation(null)
  }, [selectedPeriod[0].year, refresh])

  const [holidaysAmount, setHolidaysAmount] = useState(0)
  const { setErrorMessage } = useToast()

  useEffect(() => {
    fetchHolidaysTotal()
  }, [selectedPeriod[0].year])

  const fetchHolidaysTotal = async () => {
    try {
      const response = await getYearHolidays(selectedPeriod[0].year)
      if (!response.success) {
        throw new Error(response.message)
      }
      if (!response.result) return
      setHolidaysAmount(Object.values(response.result).flat().length)
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: "Ha ocurrido un error al obtener los días feriados.",
      })
    }
  }

  const [
    selectedFilteredCalendarVacation,
    setSelectedFilteredCalendarVacation,
  ] = useState(
    /** @type {TeamVacationRequest | null} */
    (null)
  )

  const [selectedMonth, setSelectedMonth] = useState(null)
  const initialMonthRef = useRef(null)

  useEffect(() => {
    if (!selectedMonth) return

    if (!initialMonthRef.current) {
      initialMonthRef.current = selectedMonth
    }

    const fetchDataByMonth = async () => {
      setIsLoadingTotalsSummary(true)

      try {
        const response = await getAdminVacationResumeByMonth(
          initialMonthRef.current.year,
          initialMonthRef.current.month,
          "totals"
        )

        if (!response.success) {
          throw new Error(response.message)
        }

        const result = response.result

        setTotalsSummary((prev) => ({
          ...prev,
          daysPlusToPayRequests: result.currentDays,
          overdueDaysPlusRequests: result.overdueDays,
        }))
      } catch (error) {
        console.error("Error fetching data:", error)
      } finally {
        setIsLoadingTotalsSummary(false)
      }
    }

    fetchDataByMonth()
  }, [selectedMonth, refresh])

  return (
    <>
      <ModalFormHolidaysManagement
        visible={isHolidaysModalVisible}
        setVisible={setIsHolidaysModalVisible}
        year={selectedPeriod[0].year}
        onComplete={async () => {
          await fetchData()
          triggerRefresh()
          fetchHolidaysTotal()
        }}
      />
      <VacationsHeader
        selectedPeriod={selectedPeriod}
        setSelectedPeriod={setSelectedPeriod}
        loading={isLoadingTotalsSummary}
        mode={mode}
        showHolidaysModal={() => setIsHolidaysModalVisible(true)}
        holidaysAmount={holidaysAmount}
      />
      <div className="vacation-layout">
        <div className="left-container">
          <TeamVacationRequests
            selectedPeriod={selectedPeriod}
            setVisibleModal={setVisibleModal}
            setSelectedVacationRequest={setSelectedVacationRequest}
            refresh={refresh}
            selectedVacationType={selectedVacationType}
            setSelectedVacationType={setSelectedVacationType}
            onShowOnlyThisVacation={setSelectedFilteredCalendarVacation}
            selectedFilteredCalendarVacation={selectedFilteredCalendarVacation}
          />
        </div>
        <div className="right-container">
          <AdminVacationsResume
            totalsSummary={totalsSummary}
            loading={isLoadingTotalsSummary}
            selectedMonth={selectedMonth}
            triggerRefresh={triggerRefresh}
          />
          <CalendarTeamVacationRequests
            selectedPeriod={selectedPeriod}
            selectedVacationType={selectedVacationType}
            setSelectedVacationRequest={setSelectedVacationRequest}
            setSelectedPeriod={setSelectedPeriod}
            refresh={refresh}
            shouldOnlyShowVacation={selectedFilteredCalendarVacation}
            setVisibleModal={setVisibleModal}
            deselectOnlyShowVacation={() =>
              setSelectedFilteredCalendarVacation(null)
            }
            resetSelectedPeriod={resetSelectedPeriod}
            setPreviousMonthDate={setPreviousMonthDate}
            previousMonthDate={previousMonthDate}
            setSelectedMonth={setSelectedMonth}
          />
        </div>
      </div>
      <ModalFormVacationRequestReview
        visible={showModal(visibleModal, selectedVacationRequest, [
          VACATION_STATES.PENDING,
        ])}
        setVisible={setVisibleModal}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />
      <ModalFormApprovedRequestReview
        visible={showModal(visibleModal, selectedVacationRequest, [
          VACATION_STATES.APPROVED,
          VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE,
        ])}
        setVisible={setVisibleModal}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />
      <ModalFormRejectedRequestReview
        visible={showModal(visibleModal, selectedVacationRequest, [
          VACATION_STATES.REJECTED,
          VACATION_STATES.CANCELED,
        ])}
        setVisible={setVisibleModal}
        selectedVacationRequest={selectedVacationRequest}
      />
      <ModalFormChangeRequestReview
        visible={showModal(visibleModal, selectedVacationRequest, [
          VACATION_STATES.CHANGE_REQUEST,
        ])}
        setVisible={setVisibleModal}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />
      <ModalFormCancelRequestReview
        visible={showModal(visibleModal, selectedVacationRequest, [
          VACATION_STATES.CANCEL_REQUEST,
        ])}
        setVisible={setVisibleModal}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />
    </>
  )
}

export default VacationsManagement
